// Apolo IDE — right rail preview/design panel (sprint code-preview-engine).
//
// v2: real Cursor-like preview. The Preview tab runs the session project's dev
// server and shows it live (iframe host; Electron BrowserView + CDP layer in E3).
// Console / Network / Inspect tabs surface the CDP devtools (E3, app-only). The
// Design Mode toggle (toolbar) drives the in-guest element picker (E4).

import { useEffect, useRef, useState } from 'react';
import { useAgentStore } from '../../store/useAgentStore.js';
import { useDevPreview } from '../../hooks/useDevPreview.js';
import { previewBounds } from '../../lib/previewClient.js';
import PreviewToolbar from './preview/PreviewToolbar.jsx';
import PreviewViewport from './preview/PreviewViewport.jsx';

const PARKED = { x: -100000, y: 0, width: 0, height: 0 };

// Drag handle on the rail's LEFT edge. Because the native BrowserView paints over
// the rail and eats pointer events, we PARK it off-screen the instant a drag
// starts (and `resizing` keeps PreviewViewport from re-showing it mid-drag), so
// window pointermove keeps firing; on release the viewport re-attaches at the new
// width. Keyboard ←/→ resize in 24px steps.
function ResizeHandle({ width, setWidth, onResizingChange }) {
  // Guarda os listeners do drag em voo pra poder removê-los se o componente
  // desmontar no meio do arraste (senão pointermove/pointerup vazam no window).
  const dragRef = useRef(null);
  useEffect(
    () => () => {
      if (dragRef.current) {
        window.removeEventListener('pointermove', dragRef.current.onMove);
        window.removeEventListener('pointerup', dragRef.current.onUp);
        dragRef.current = null;
      }
    },
    [],
  );

  const onPointerDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    onResizingChange(true);
    previewBounds({ bounds: PARKED });
    const onMove = (ev) => setWidth(startW + (startX - ev.clientX));
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      dragRef.current = null;
      onResizingChange(false);
    };
    dragRef.current = { onMove, onUp };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Redimensionar painel de preview"
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); setWidth(width + 24); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); setWidth(width - 24); }
      }}
      data-testid="preview-resize-handle"
      title="Arraste para redimensionar (←/→ no teclado)"
      className="group absolute left-0 top-0 z-20 flex h-full w-2.5 cursor-ew-resize items-center justify-center bg-transparent transition hover:bg-apolo-accent/15 focus:outline-none focus-visible:bg-apolo-accent/25"
    >
      <span className="h-8 w-0.5 rounded-full bg-apolo-borderStrong transition group-hover:bg-apolo-accent" aria-hidden="true" />
    </div>
  );
}

export default function PreviewPanel({ open, onClose }) {
  const activeSessionId = useAgentStore((s) => s.code.activeSessionId);
  // The Code tab stays MOUNTED when you switch tabs (AppShell keep-alive: inactive
  // views are display:none, not unmounted). A native BrowserView ignores CSS display,
  // so once a URL is loaded the view kept painting over Council/GOD/etc. Park it while
  // the Code tab isn't the active workspace — reusing the same off-screen park that
  // drag-resize uses — so it re-attaches instantly on return.
  const codeTabActive = useAgentStore((s) => s.activeWorkspace === 'code');
  // Only bind to a session (and subscribe to IPC) when the rail is open.
  const p = useDevPreview(open ? activeSessionId : null);
  const [resizing, setResizing] = useState(false);
  const [pointerMenuOpen, setPointerMenuOpen] = useState(false);

  if (!open) return null;

  return (
    <aside
      className="relative hidden h-full shrink-0 flex-col border-l border-apolo-borderSubtle bg-apolo-surface1 lg:flex"
      style={{
        width: p.width,
        // (Pedro) minimal/neutral preview chrome — flip the blue accent to a soft
        // near-white so tabs / run-controls / focus-rings read MONOCHROME, like an
        // Arc/Dia browser. PreviewPanel mounts OUTSIDE .rx-shell and inherits the
        // global blue; scoping the accent var here recolors every child at once
        // (toolbar, tabs, console, network, inspect) via the CSS-var cascade.
        // Status colors (success/warn/error) use separate tokens — untouched.
        '--apolo-accent': '#e6e6e6',
        '--apolo-accentHover': '#ffffff',
      }}
      aria-label="Painel de preview e design"
      data-testid="preview-panel"
    >
      <ResizeHandle width={p.width} setWidth={p.setWidth} onResizingChange={setResizing} />
      {/* Single browser-chrome header (Pedro): the URL searchbar lives up top next to the run
          controls + element picker + close. The Console/Network/Inspect tabs and the screenshot
          camera were removed — the panel is just a clean live preview of the dev server. */}
      <PreviewToolbar
        state={p.state}
        url={p.url}
        supported={p.supported}
        manualUrl={p.manualUrl}
        setManualUrl={p.setManualUrl}
        commitUrl={p.commitUrl}
        device={p.device}
        setDevice={p.setDevice}
        annotateModeOn={p.annotateModeOn}
        toggleAnnotateMode={p.toggleAnnotateMode}
        designModeOn={p.designModeOn}
        toggleDesignMode={p.toggleDesignMode}
        onPointerMenuOpenChange={setPointerMenuOpen}
        onStart={p.start}
        onStop={p.stop}
        onRestart={p.restart}
        onClose={onClose}
      />

      {/* menu do pointer aberto ⇒ pausa o guest (BrowserView nativa engole HTML) */}
      <PreviewViewport url={p.url} device={p.device} state={p.state} paused={resizing || !codeTabActive || pointerMenuOpen} />
    </aside>
  );
}
